import { Plus, X, ShoppingCart } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'

import { toast } from '@/shared/lib/toast'

import {
  generateNextPurchaseOrderNumber,
  formatCurrency,
} from '../utils/purchaseOrder.utils'

import { PurchaseOrderItemRow } from './PurchaseOrderItemRow'
import { PurchaseOrderTermsConditions } from './PurchaseOrderTermsConditions'
import { VendorInfoCard } from './VendorInfoCard'

const PAYMENT_TERMS = [
  'Advance',
  'Immediate',
  'Against delivery',
  'One week',
  '15 days',
  '21 days',
  '30 days',
  '45 days',
  '60 days',
  'Custom',
]

const DELIVERY_TERMS = ['Door delivery', 'Warehouse pickup', 'Custom']

const ZERO_TAX_TYPES = ['EXPORT_SEZ_BOND', 'EXPORT_SEZ_NO_TAX']

const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  10: 'Bihar',
  11: 'Sikkim',
  12: 'Arunachal Pradesh',
  13: 'Nagaland',
  14: 'Manipur',
  15: 'Mizoram',
  16: 'Tripura',
  17: 'Meghalaya',
  18: 'Assam',
  19: 'West Bengal',
  20: 'Jharkhand',
  21: 'Odisha',
  22: 'Chhattisgarh',
  23: 'Madhya Pradesh',
  24: 'Gujarat',
  26: 'Dadra and Nagar Haveli and Daman and Diu',
  27: 'Maharashtra',
  29: 'Karnataka',
  30: 'Goa',
  31: 'Lakshadweep',
  32: 'Kerala',
  33: 'Tamil Nadu',
  34: 'Puducherry',
  35: 'Andaman and Nicobar Islands',
  36: 'Telangana',
  37: 'Andhra Pradesh',
  38: 'Ladakh',
}

function resolveVendorState(vendor) {
  if (!vendor) return ''
  if (vendor.state?.trim()) return vendor.state.trim()

  const gst = vendor.gstNumber?.trim() || ''
  if (gst.length >= 2) {
    const code = gst.slice(0, 2)
    if (GST_STATE_CODES[code]) {
      return GST_STATE_CODES[code]
    }
  }

  const address = vendor.address?.trim() || ''
  if (address) {
    for (const stateName of Object.values(GST_STATE_CODES)) {
      const regex = new RegExp(`\\b${stateName}\\b`, 'i')
      if (regex.test(address)) {
        return stateName
      }
    }
    return address
  }

  return ''
}

const createInitialItem = () => ({
  detail: '',
  hsnCode: '',
  quantity: '1',
  unitPrice: '',
  gstType: '',
  gstValue: '',
})

export function CreatePurchaseOrderModal(props) {
  if (!props.isOpen) return null

  return (
    <CreatePurchaseOrderModalContent
      key={props.initialVendor?._id || 'new-purchase-order'}
      {...props}
    />
  )
}

function CreatePurchaseOrderModalContent({
  isOpen,
  onClose,
  initialVendor = null,
  vendors = [],
  purchaseOrders = [],
  onSubmit,
  isSubmitting = false,
  fetchNextNumber,
}) {
  const [formData, setFormData] = useState(() => {
    const initialDate = new Date().toISOString().slice(0, 10)

    return {
      purchaseOrderNumber: generateNextPurchaseOrderNumber(
        purchaseOrders,
        initialDate,
      ),
      paymentTermsCustom: '',
      deliveryTermsCustom: '',
      purchaseOrderDate: initialDate,
      vendor: initialVendor?._id || '',
      paymentTerms: '',
      deliveryTerms: '',
      items: [createInitialItem()],
    }
  })

  useEffect(() => {
    if (!isOpen || typeof fetchNextNumber !== 'function') return

    const initialDate = formData.purchaseOrderDate

    fetchNextNumber(initialDate).then((backendPoNo) => {
      if (backendPoNo) {
        setFormData((prev) => ({
          ...prev,
          purchaseOrderNumber: backendPoNo,
        }))
      }
    })
  }, [isOpen, fetchNextNumber, formData.purchaseOrderDate])

  // Resolve active vendor details (either from initialVendor prop or found in vendors list)
  const activeVendor = useMemo(() => {
    if (initialVendor && initialVendor._id === formData.vendor) {
      return initialVendor
    }
    return vendors.find((v) => v._id === formData.vendor) || null
  }, [formData.vendor, initialVendor, vendors])

  // Calculate order totals
  const totals = useMemo(() => {
    let subtotal = 0
    let totalTax = 0
    let totalQty = 0

    formData.items.forEach((item) => {
      const q = Number(item.quantity) || 0
      const p = Number(item.unitPrice) || 0
      const g = Number(item.gstValue) || 0

      const base = q * p
      const tax = (base * g) / 100

      totalQty += q
      subtotal += base
      totalTax += tax
    })

    return {
      totalQty,
      subtotal,
      totalTax,
      grandTotal: subtotal + totalTax,
    }
  }, [formData.items])

  const isExportOrNoTax = useMemo(
    () => formData.items.some((item) => ZERO_TAX_TYPES.includes(item.gstType)),
    [formData.items],
  )

  const vendorState = useMemo(
    () => resolveVendorState(activeVendor),
    [activeVendor],
  )

  const gstApplicability = isExportOrNoTax
    ? vendorState
      ? `No tax applicable for ${vendorState}.`
      : 'No tax applicable.'
    : ''

  if (!isOpen) return null

  const handleFieldChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => {
      const next = { ...prev, [name]: value }

      // If purchaseOrderDate changes, optionally refresh PO number if matching format
      if (name === 'purchaseOrderDate') {
        const nextPo = generateNextPurchaseOrderNumber(purchaseOrders, value)
        if (prev.purchaseOrderNumber.startsWith('SDPL/PO/')) {
          next.purchaseOrderNumber = nextPo
        }
      }

      return next
    })
  }

  const handleItemChange = (index, e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [name]: value } : item,
      ),
    }))
  }

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, createInitialItem()],
    }))
  }

  const handleRemoveItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.items.length) {
      toast.error('At least one item is required')
      return
    }

    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i]
      if (!item.detail?.trim()) {
        toast.error(`Item ${i + 1}: Product Details is required`)
        return
      }
      if (!/^[a-zA-Z0-9\s.,'()-]+$/.test(item.detail.trim())) {
        toast.error(
          `Item ${i + 1}: Product Details must accept alphanumeric characters`,
        )
        return
      }
      if (!item.hsnCode?.trim()) {
        toast.error(`Item ${i + 1}: HSN Code is required`)
        return
      }
      if (!/^\d+$/.test(item.hsnCode.trim())) {
        toast.error(`Item ${i + 1}: HSN Code must accept only numbers`)
        return
      }
      if (
        item.quantity === '' ||
        isNaN(Number(item.quantity)) ||
        Number(item.quantity) <= 0
      ) {
        toast.error(
          `Item ${i + 1}: Quantity must accept only numbers and be greater than 0`,
        )
        return
      }
      if (
        item.unitPrice === '' ||
        isNaN(Number(item.unitPrice)) ||
        Number(item.unitPrice) < 0
      ) {
        toast.error(`Item ${i + 1}: Unit Price must accept only numbers`)
        return
      }
      if (!item.gstType) {
        toast.error(`Item ${i + 1}: GST Type is required`)
        return
      }
      if (!ZERO_TAX_TYPES.includes(item.gstType)) {
        if (
          item.gstValue === '' ||
          isNaN(Number(item.gstValue)) ||
          Number(item.gstValue) < 0
        ) {
          toast.error(`Item ${i + 1}: GST Value must accept only numbers`)
          return
        }
      }
    }

    const payload = {
      ...formData,
      paymentTerms:
        formData.paymentTerms === 'Custom'
          ? formData.paymentTermsCustom.trim()
          : formData.paymentTerms,

      deliveryTerms:
        formData.deliveryTerms === 'Custom'
          ? formData.deliveryTermsCustom.trim()
          : formData.deliveryTerms,

      gstApplicability,

      items: formData.items.map((item) => ({
        ...item,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        gstValue: Number(item.gstValue),
      })),
    }

    onSubmit(payload)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-xs sm:p-5"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-po-title"
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4.5">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-2xl bg-[#080d25] text-white">
              <ShoppingCart className="size-5" />
            </div>
            <div>
              <h2
                id="create-po-title"
                className="text-lg font-bold tracking-tight text-[#080d25]"
              >
                Create Purchase Order
              </h2>
            </div>
          </div>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            aria-label="Close modal"
            className="flex size-9 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition disabled:opacity-50"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Modal Body / Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6"
        >
          {/* Vendor Details Card (Date, GST Number, Address) */}
          <VendorInfoCard
            vendor={activeVendor}
            date={formData.purchaseOrderDate}
          />

          {/* Primary Order Fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* PO Number with auto-generate indicator */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="purchaseOrderNumber"
                  className="block text-xs font-semibold uppercase tracking-wider text-slate-700"
                >
                  Purchase Order Number <span className="text-red-500">*</span>
                </label>
                <span className="text-[11px] font-medium text-slate-400">
                  Auto-generated
                </span>
              </div>
              <input
                id="purchaseOrderNumber"
                name="purchaseOrderNumber"
                type="text"
                value={formData.purchaseOrderNumber}
                readOnly
                tabIndex={-1}
                required
                placeholder="SDPL/PO/26-27/2145"
                className="w-full rounded-xl border border-slate-200 bg-slate-100/80 font-mono text-sm font-semibold text-slate-600 px-3.5 py-2.5 shadow-xs cursor-not-allowed select-none focus:outline-none"
              />
            </div>

            {/* Vendor Selector */}
            <div>
              <label
                htmlFor="po-vendor"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-700"
              >
                Vendor <span className="text-red-500">*</span>
              </label>
              <select
                id="po-vendor"
                name="vendor"
                value={formData.vendor}
                onChange={handleFieldChange}
                required
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-900 shadow-xs transition-colors focus:border-[#080d25] focus:outline-none focus:ring-2 focus:ring-[#080d25]/10"
              >
                <option value="">Select a vendor</option>
                {vendors.map((vendor) => (
                  <option key={vendor._id} value={vendor._id}>
                    {vendor.vendorName}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Terms */}
            <div>
              <label
                htmlFor="po-payment-terms"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-700"
              >
                Payment Terms <span className="text-red-500">*</span>
              </label>
              <select
                id="po-payment-terms"
                name="paymentTerms"
                value={formData.paymentTerms}
                onChange={handleFieldChange}
                required
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-normal text-slate-900 shadow-xs transition-colors focus:border-[#080d25] focus:outline-none focus:ring-2 focus:ring-[#080d25]/10"
              >
                <option value="">Select payment terms</option>
                {PAYMENT_TERMS.map((term) => (
                  <option key={term} value={term}>
                    {term}
                  </option>
                ))}
              </select>

              {formData.paymentTerms === 'Custom' && (
                <input
                  type="text"
                  name="paymentTermsCustom"
                  value={formData.paymentTermsCustom}
                  onChange={handleFieldChange}
                  required
                  placeholder="Enter custom payment terms"
                  aria-label="Custom payment terms"
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-xs focus:border-[#080d25] focus:outline-none focus:ring-2 focus:ring-[#080d25]/10"
                />
              )}
            </div>

            {/* Delivery Terms */}
            <div>
              <label
                htmlFor="po-delivery-terms"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-700"
              >
                Delivery Terms <span className="text-red-500">*</span>
              </label>
              <select
                id="po-delivery-terms"
                name="deliveryTerms"
                value={formData.deliveryTerms}
                onChange={handleFieldChange}
                required
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-normal text-slate-900 shadow-xs transition-colors focus:border-[#080d25] focus:outline-none focus:ring-2 focus:ring-[#080d25]/10"
              >
                <option value="">Select delivery terms</option>
                {DELIVERY_TERMS.map((term) => (
                  <option key={term} value={term}>
                    {term}
                  </option>
                ))}
              </select>

              {formData.deliveryTerms === 'Custom' && (
                <input
                  type="text"
                  name="deliveryTermsCustom"
                  value={formData.deliveryTermsCustom}
                  onChange={handleFieldChange}
                  required
                  placeholder="Enter custom delivery terms"
                  aria-label="Custom delivery terms"
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-xs focus:border-[#080d25] focus:outline-none focus:ring-2 focus:ring-[#080d25]/10"
                />
              )}
            </div>
          </div>

          {/* Products / Items Section */}
          <div className="space-y-3.5 border-t border-slate-200 pt-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#080d25]">
                  Products & Line Items
                </h3>
                <p className="text-xs text-slate-500">
                  Specify details, HSN codes, and GST rates for each item.
                </p>
              </div>

              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition"
              >
                <Plus className="size-3.5" />
                Add Product
              </button>
            </div>

            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <PurchaseOrderItemRow
                  key={index}
                  index={index}
                  item={item}
                  onChange={handleItemChange}
                  onRemove={handleRemoveItem}
                  canRemove={formData.items.length > 1}
                />
              ))}
            </div>
          </div>
          <PurchaseOrderTermsConditions
            showGstApplicability={isExportOrNoTax}
            gstApplicability={gstApplicability}
          />
          {/* Order Totals Summary */}
          <div className="flex flex-col items-end gap-2 rounded-2xl bg-slate-50 p-4 border border-slate-200">
            <div className="w-full max-w-xs space-y-1.5 text-xs text-slate-600">
              <div className="flex justify-between">
                <span>Total Items:</span>
                <span className="font-semibold text-slate-900">
                  {formData.items.length} ({totals.totalQty} units)
                </span>
              </div>
              <div className="flex justify-between">
                <span>Subtotal (before GST):</span>
                <span className="font-semibold text-slate-900">
                  {formatCurrency(totals.subtotal)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total GST Amount:</span>
                <span className="font-semibold text-slate-900">
                  {formatCurrency(totals.totalTax)}
                </span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-2 text-sm font-bold text-[#080d25]">
                <span>Grand Total:</span>
                <span className="text-base text-indigo-700">
                  {formatCurrency(totals.grandTotal)}
                </span>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#080d25] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#17203e] disabled:cursor-not-allowed disabled:opacity-60 transition shadow-sm"
            >
              {isSubmitting ? 'Creating PO...' : 'Create PO'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
